import React, { useEffect, useMemo, useState } from "react"
import { Helmet } from "react-helmet-async"
import toast from "react-hot-toast"
import {
  Plus,
  Search,
  Download,
  Trash2,
  Phone as PhoneIcon,
  MessageCircle,
  X,
  LogOut,
  Users,
  Sparkles,
  TrendingUp,
  Trophy,
} from "lucide-react"
import AccessGate, { LEADS_UNLOCK_KEY } from "../components/leads/AccessGate"
import ProspectFinder from "../components/leads/ProspectFinder"
import { leadsStorage } from "../services/leadsStorage"
import {
  type Lead,
  type LeadStatus,
  type LeadSource,
  type NewLeadInput,
  LEAD_STATUSES,
  LEAD_SOURCES,
  FURNITURE_INTERESTS,
  BUDGET_RANGES,
  TIMELINES,
} from "../types/lead"

const emptyForm: NewLeadInput = {
  name: "",
  phone: "",
  city: "",
  interest: FURNITURE_INTERESTS[0],
  budget: BUDGET_RANGES[0],
  timeline: TIMELINES[0],
  source: "walk-in",
  notes: "",
}

function digitsOnly(phone: string): string {
  return phone.replace(/\D/g, "")
}

// Best-effort normalize to a 91-prefixed Indian mobile number for tel:/wa.me links.
function toIndianE164(phone: string): string {
  const d = digitsOnly(phone)
  if (d.length === 10) return `91${d}`
  if (d.length === 12 && d.startsWith("91")) return d
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`
  return d
}

function whatsappLink(lead: Lead): string {
  const number = toIndianE164(lead.phone)
  const message = encodeURIComponent(
    `Hi ${lead.name}, this is Anmol Furniture Bazar following up on your enquiry about ${lead.interest}.`,
  )
  return `https://wa.me/${number}?text=${message}`
}

function telLink(phone: string): string {
  return `tel:+${toIndianE164(phone)}`
}

const statusStyles: Record<LeadStatus, string> = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  quoted: "bg-purple-100 text-purple-700",
  won: "bg-green-100 text-green-700",
  lost: "bg-gray-200 text-gray-600",
}

const colorMap = {
  primary: "bg-primary-100 text-primary-600",
  blue: "bg-blue-100 text-blue-600",
  amber: "bg-amber-100 text-amber-600",
  green: "bg-green-100 text-green-600",
} as const

interface StatCardProps {
  icon: React.ElementType
  label: string
  value: number
  color: keyof typeof colorMap
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex items-center gap-4">
    <div
      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorMap[color]}`}
    >
      <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
    </div>
    <div>
      <div className="text-xl sm:text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs sm:text-sm text-gray-500">{label}</div>
    </div>
  </div>
)

const Leads: React.FC = () => {
  const [unlocked, setUnlocked] = useState(false)
  const [leads, setLeads] = useState<Lead[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [form, setForm] = useState<NewLeadInput>(emptyForm)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | LeadStatus>("all")
  const [tab, setTab] = useState<"leads" | "find">("leads")

  useEffect(() => {
    if (localStorage.getItem(LEADS_UNLOCK_KEY) === "true") {
      setUnlocked(true)
    }
  }, [])

  const refresh = () => setLeads(leadsStorage.getAll())

  useEffect(() => {
    if (unlocked) refresh()
  }, [unlocked])

  const stats = useMemo(
    () => ({
      total: leads.length,
      new: leads.filter((l) => l.status === "new").length,
      inProgress: leads.filter((l) => l.status === "contacted" || l.status === "quoted").length,
      won: leads.filter((l) => l.status === "won").length,
    }),
    [leads],
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return leads.filter((l) => {
      const matchesSearch =
        !term || [l.name, l.phone, l.city, l.notes].some((field) => field.toLowerCase().includes(term))
      const matchesStatus = statusFilter === "all" || l.status === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [leads, search, statusFilter])

  const handleLock = () => {
    localStorage.removeItem(LEADS_UNLOCK_KEY)
    setUnlocked(false)
  }

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required")
      return
    }
    leadsStorage.add(form)
    refresh()
    setForm(emptyForm)
    setShowAddForm(false)
    toast.success("Lead added")
  }

  const handleStatusChange = (id: string, status: LeadStatus) => {
    leadsStorage.updateStatus(id, status)
    refresh()
  }

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Delete lead "${name}"? This can't be undone.`)) return
    leadsStorage.remove(id)
    refresh()
    toast.success("Lead deleted")
  }

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.error("No leads to export")
      return
    }
    leadsStorage.exportToExcel(filtered)
  }

  const handleProspectsAdded = () => {
    refresh()
    setTab("leads")
  }

  if (!unlocked) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <AccessGate onUnlock={() => setUnlocked(true)} />
      </>
    )
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Lead Management</h1>
              <p className="text-gray-500 text-sm mt-1">
                Track walk-ins, calls and enquiries in one place. Saved on this device only — export regularly to
                back up.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {tab === "leads" && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                >
                  <Plus className="w-4 h-4" /> Add Lead
                </button>
              )}
              <button
                onClick={handleLock}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 px-3 py-2.5 rounded-lg border border-gray-300 text-sm"
                aria-label="Lock dashboard"
              >
                <LogOut className="w-4 h-4" /> Lock
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-gray-200">
            <button
              onClick={() => setTab("leads")}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === "leads"
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              My Leads
            </button>
            <button
              onClick={() => setTab("find")}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === "find"
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Find Prospects
            </button>
          </div>

          {tab === "find" && <ProspectFinder onAdded={handleProspectsAdded} />}

          {tab === "leads" && (
            <>
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Leads" value={stats.total} color="primary" />
            <StatCard icon={Sparkles} label="New" value={stats.new} color="blue" />
            <StatCard icon={TrendingUp} label="In Progress" value={stats.inProgress} color="amber" />
            <StatCard icon={Trophy} label="Won" value={stats.won} color="green" />
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, city or notes..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | LeadStatus)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All statuses</option>
              {LEAD_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleExport}
              className="flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              <Download className="w-4 h-4" /> Export
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-16 px-4">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No leads yet</p>
                <p className="text-gray-400 text-sm mt-1">Add your first lead to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Name</th>
                      <th className="text-left px-4 py-3 font-medium">Contact</th>
                      <th className="text-left px-4 py-3 font-medium">Interest</th>
                      <th className="text-left px-4 py-3 font-medium">Budget</th>
                      <th className="text-left px-4 py-3 font-medium">Source</th>
                      <th className="text-left px-4 py-3 font-medium">Status</th>
                      <th className="text-left px-4 py-3 font-medium">Added</th>
                      <th className="text-right px-4 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{lead.name}</div>
                          {lead.city && <div className="text-xs text-gray-400">{lead.city}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{lead.phone}</td>
                        <td className="px-4 py-3 text-gray-600">{lead.interest}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{lead.budget}</td>
                        <td className="px-4 py-3 text-gray-600 capitalize whitespace-nowrap">
                          {lead.source.replace("-", " ")}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={lead.status}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value as LeadStatus)}
                            className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-primary-500 ${statusStyles[lead.status]}`}
                          >
                            {LEAD_STATUSES.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {new Date(lead.createdAt).toLocaleDateString("en-IN")}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <a
                              href={whatsappLink(lead)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              aria-label="Message on WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                            <a
                              href={telLink(lead.phone)}
                              className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                              aria-label="Call"
                            >
                              <PhoneIcon className="w-4 h-4" />
                            </a>
                            <button
                              onClick={() => handleDelete(lead.id, lead.name)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              aria-label="Delete lead"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* Add Lead Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10003] p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowAddForm(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="bg-primary-600 text-white p-6 rounded-t-lg">
                <h2 className="text-xl font-bold">Add New Lead</h2>
              </div>
              <form onSubmit={handleAdd} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                    <select
                      value={form.source}
                      onChange={(e) => setForm((f) => ({ ...f, source: e.target.value as LeadSource }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {LEAD_SOURCES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Interested In</label>
                    <select
                      value={form.interest}
                      onChange={(e) => setForm((f) => ({ ...f, interest: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {FURNITURE_INTERESTS.map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
                    <select
                      value={form.budget}
                      onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {BUDGET_RANGES.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                  <select
                    value={form.timeline}
                    onChange={(e) => setForm((f) => ({ ...f, timeline: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {TIMELINES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-5 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                  >
                    Save Lead
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </>
  )
}

export default Leads
